import React, { useCallback, useEffect, useState } from "react";
import { adminApi } from "@/lib/adminApi";
import { AlertTriangle, Ban, Check, Trash2 } from "lucide-react";
import { toast } from "sonner";

const SEV_STYLE = {
    creepy: { color: "#F59E0B", label: "creepy" },
    predatory: { color: "#EF4444", label: "predatory" },
    mild: { color: "#94A3B8", label: "mild" },
};

export default function AdminModeration() {
    const [severity, setSeverity] = useState("");
    const [resolved, setResolved] = useState("false");
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { page: 1, limit: 100 };
            if (severity) params.severity = severity;
            if (resolved !== "") params.resolved = resolved === "true";
            const { data } = await adminApi.get("/admin/moderation", { params });
            setRows(data.messages || []);
            setTotal(data.total || 0);
        } catch (e) { toast.error("Couldn't load moderation queue"); }
        finally { setLoading(false); }
    }, [severity, resolved]);
    useEffect(() => { load(); }, [load]);

    const act = async (msgId, action, reason) => {
        try {
            await adminApi.post(`/admin/moderation/${msgId}/action`, { action, reason });
            toast.success(action);
            load();
        } catch (e) { toast.error("Failed"); }
    };

    return (
        <div data-testid="admin-moderation-page">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="font-display text-3xl font-black">Moderation queue</h1>
                    <p className="text-sm text-white/50">{total} messages flagged</p>
                </div>
            </div>

            <div className="mt-4 rounded-2xl border border-white/10 bg-white/5 p-3 flex flex-wrap gap-2">
                <select value={severity} onChange={(e) => setSeverity(e.target.value)}
                    className="h-9 px-3 rounded-lg bg-white/5 border border-white/10 text-sm outline-none">
                    <option value="">All severities</option>
                    <option value="predatory">Predatory only</option>
                    <option value="creepy">Creepy only</option>
                    <option value="mild">Mild</option>
                </select>
                <select value={resolved} onChange={(e) => setResolved(e.target.value)}
                    className="h-9 px-3 rounded-lg bg-white/5 border border-white/10 text-sm outline-none">
                    <option value="false">Pending only</option>
                    <option value="true">Resolved</option>
                    <option value="">All</option>
                </select>
            </div>

            <div className="mt-4 space-y-3" data-testid="admin-moderation-list">
                {loading ? (
                    <div className="text-white/50 text-sm">Loading…</div>
                ) : rows.length === 0 ? (
                    <div className="rounded-2xl border-2 border-dashed border-white/10 p-8 text-center text-white/50">
                        <div className="text-4xl mb-2">🛡️</div>
                        Nothing flagged. Good vibes only.
                    </div>
                ) : rows.map((m) => {
                    const sev = SEV_STYLE[m.moderation_status] || SEV_STYLE.mild;
                    return (
                        <div key={m.id} className="rounded-2xl border border-white/10 bg-white/5 p-4" data-testid={`admin-mod-row-${m.id}`}>
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-[10px] font-black uppercase tracking-widest rounded-full px-2 py-0.5 flex items-center gap-1"
                                    style={{ background: `${sev.color}30`, color: sev.color }}>
                                    <AlertTriangle className="h-3 w-3" strokeWidth={3} /> {sev.label}
                                </span>
                                <span className="text-xs text-white/50">{(m.created_at || "").slice(0, 16).replace("T", " ")}</span>
                                {m.moderation_reason && (
                                    <span className="text-xs text-white/60 italic">— {m.moderation_reason}</span>
                                )}
                            </div>
                            <div className="mt-2 flex items-center gap-2 text-xs text-white/70">
                                <span className="font-semibold">{m.from_user?.name || m.from_user?.email || "?"}</span>
                                <span className="opacity-60">→</span>
                                <span className="font-semibold">{m.to_user?.name || m.to_user?.email || "?"}</span>
                            </div>
                            <div className="mt-2 rounded-xl bg-black/40 p-3 text-sm text-white/90 break-words">
                                {m.body}
                            </div>
                            <div className="mt-3 flex flex-wrap gap-2">
                                <button onClick={() => act(m.id, "dismiss")} data-testid={`admin-mod-dismiss-${m.id}`}
                                    className="h-9 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 px-3 text-xs font-semibold flex items-center gap-1.5">
                                    <Check className="h-3.5 w-3.5" /> Dismiss (mark safe)
                                </button>
                                <button onClick={() => act(m.id, "ban_sender", `Predatory chat: "${m.body.slice(0, 60)}"`)} data-testid={`admin-mod-ban-${m.id}`}
                                    className="h-9 rounded-lg bg-[#EF4444]/20 border border-[#EF4444]/40 hover:bg-[#EF4444]/30 px-3 text-xs font-semibold text-[#EF4444] flex items-center gap-1.5">
                                    <Ban className="h-3.5 w-3.5" /> Ban sender
                                </button>
                                <button onClick={() => act(m.id, "delete_message")} data-testid={`admin-mod-delete-${m.id}`}
                                    className="h-9 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 px-3 text-xs font-semibold flex items-center gap-1.5">
                                    <Trash2 className="h-3.5 w-3.5" /> Delete message
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
